/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useState, useEffect, useCallback, useMemo} from 'react'
import PropTypes from 'prop-types'
import {useHistory, useLocation} from 'react-router-dom'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import useActiveData from '@salesforce/retail-react-app/app/hooks/use-active-data'
import {useQuery} from '@tanstack/react-query'
import {
    useAccessToken,
    useCategory,
    useShopperBasketsMutation
} from '@salesforce/commerce-sdk-react'
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'

// Chakra
import {
    Box,
    useDisclosure,
    useStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {SkipNavLink, SkipNavContent} from '@chakra-ui/skip-nav'

// Contexts
import {CurrencyProvider} from '@salesforce/retail-react-app/app/contexts'

// Local Project Components
// import Header from '../header'
// import Header from '@salesforce/retail-react-app/app/components/header'
import Header from '../header-v2'
import OfflineBanner from '@salesforce/retail-react-app/app/components/offline-banner'
import OfflineBoundary from '@salesforce/retail-react-app/app/components/offline-boundary'
import ScrollToTop from '@salesforce/retail-react-app/app/components/scroll-to-top'
import Footer from '../footer'
import CheckoutHeader from '../../pages/checkout/partials/checkout-header'
import CheckoutFooter from '@salesforce/retail-react-app/app/pages/checkout/partials/checkout-footer'
import {DrawerMenu} from '../drawer-menu/drawer-menu'
import {MegaMenu} from '../mega-menu'
import {MegaMenuContent} from '../mega-menu/mega-menu-content'
// import {ListMenuContent} from '../list-menu/list-menu-content'
import {HideOnDesktop, HideOnMobileV2} from '../responsive'
import AboveHeader from './partials/above-header'
// Hooks
import {AuthModal, useAuthModal} from '../../hooks/use-auth-modal'
import {AddToCartModalProvider} from '../../hooks/use-add-to-cart-modal'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {useUpdateShopperContext} from '../../hooks/use-update-shopper-context'

// Localization
import {IntlProvider} from 'react-intl'

// Others
import {watchOnlineStatus, flatten, isServer} from '@salesforce/retail-react-app/app/utils/utils'
import {getTargetLocale, fetchTranslations} from '@salesforce/retail-react-app/app/utils/locale'
import {
    DEFAULT_SITE_TITLE,
    THEME_COLOR,
    CAT_MENU_DEFAULT_ROOT_CATEGORY,
    DEFAULT_LOCALE,
    ACTIVE_DATA_ENABLED
} from '@salesforce/retail-react-app/app/constants'

import Seo from '@salesforce/retail-react-app/app/components/seo'
import {Helmet} from 'react-helmet'
import BootstrapProvider from '../bootstrap-provider'
import Fonts from '../../static/fonts/fonts'
import {ListMenuMainContent} from '../list-menu/list-menu-main-content'
import {zIndex} from '../../theme/foundations/zIndex'
import size from '../../utills/size'
import {pxToRem} from '../../utills/pxToRem'
import {HEADER_PROMO_BANNER_HEIGHT, PRODUCTION_URL} from '../../constants'
import useGoogleTagManager from '../../utills/useGoogleTagManager'
import useContentClickTracking from '../../utills/gtm/useContentClickTracking'
import usePromotionTracking from '../../utills/gtm/usePromotionTracking'
import useLayoutShiftReporting from '../../utills/gtm/useLayoutShiftReporting'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useSitePreference from '../../hooks/use-site-preference'
import PromoBanner from './partials/promo-banner'
import ImpactTracker from '../impact-tracker'
import BazaarvoiceTracker from '../bazaarvoice-tracker'
import {LoginProvider} from '../login/loginContext'
import {StorefrontPreviewProvider} from '../../contexts/StorefrontPreviewContext'
import {hasAnalyticsConsent, subscribeToConsentChanges} from '../../utills/ketchConsentManager'

const App = (props) => {
    const {children} = props
    const {data: categoriesTree} = useCategory({
        parameters: {
            id: CAT_MENU_DEFAULT_ROOT_CATEGORY,
            levels: 3,
            locale: 'default'
        }
    })

    const categories = flatten(categoriesTree || {}, 'categories')
    const {getTokenWhenReady} = useAccessToken()
    const appOrigin = useAppOrigin()
    const activeData = useActiveData()
    const {app: appConfig} = getConfig()
    const gtmContainerID = appConfig.gtmData?.containerID
    const gtmAuth = appConfig.gtmData?.gtmAuth
    const gtmPreview = appConfig.gtmData?.gtmPreview
    const userWayAccountId = appConfig.userWay?.enabled ? appConfig.userWay?.accountId : null
    const userWayPosition = appConfig.userWay?.position
    const userWayColor = appConfig.userWay?.color
    const history = useHistory()
    const location = useLocation()
    const authModal = useAuthModal()
    const {site, locale, buildUrl} = useMultiSite()
    const isImpactTrackingEnabled =
        useSitePreference('isImpactTrackingEnabled')?.data?.isImpactTrackingEnabled
    const isSignifydCartridgeEnabled =
        useSitePreference('SignifydEnableCartridge')?.data?.SignifydEnableCartridge

    const [isOnline, setIsOnline] = useState(true)
    const styles = useStyleConfig('App')
    const {isOpen, onOpen, onClose} = useDisclosure()

    const [menuToggle, setMenuToggle] = useState(false)

    const [isStickyActive, setIsStickyActive] = useState(false)

    const [promoBannerInHeaderIsActive, setPromoBannerInHeaderIsActive] = useState(false)
    const [hasConsent, setHasConsent] = useState(false)

    const targetLocale = getTargetLocale({
        getUserPreferredLocales: () => {
            // CONFIG: This function should return an array of preferred locales. They can be
            // derived from various sources. Below are some examples of those:
            //
            // - client side: window.navigator.languages
            // - the page URL they're on (example.com/en-GB/home)
            // - cookie (if their previous preference is saved there)
            //
            // If this function returns an empty array (e.g. there isn't locale in the page url),
            // then the app would use the default locale as the fallback.

            // NOTE: Your implementation may differ, this is just what we did.
            return [locale?.id || DEFAULT_LOCALE]
        },
        l10nConfig: site.l10n
    })

    // If the translation file exists, it'll be served directly from static folder (and won't reach this code here).
    // However, if the file is missing, the App would render a 404 page.
    const is404ForMissingTranslationFile = /\/static\/translations\/compiled\/[^.]+\.json$/.test(
        location?.pathname
    )

    // Fetch the translation message data using the target locale.
    const {data: messages} = useQuery({
        queryKey: ['app', 'translations', 'messages', targetLocale],
        queryFn: () => {
            if (is404ForMissingTranslationFile) {
                // Return early to prevent an infinite loop
                // Otherwise, it'll continue to fetch the missing translation file again
                return {}
            }
            return fetchTranslations(targetLocale, appOrigin)
        },
        enabled: isServer
    })

    // Used to conditionally render header/footer for checkout page
    const isCheckoutAndConfirmation = /^\/checkout(\/(confirmation|paypal)(\/[^/]+)?)?\/?$/.test(
        location?.pathname
    )
    const isCheckout = /\/checkout$/.test(location?.pathname)

    const {l10n} = site
    // Get the current currency to be used through out the app
    const currency = locale.preferredCurrency || l10n.defaultCurrency

    // Handle creating a new basket if there isn't one already assigned to the current
    // customer.
    const {data: customer} = useCurrentCustomer()
    const {data: basket} = useCurrentBasket()

    // Compute the Signifyd order session ID, derived from basket id when available
    // environmentId is read from getConfig() which is populated from process.env at build time
    // and safely available in both SSR and browser contexts
    const [signifydOrderSessionId, setSignifydOrderSessionId] = useState(null)

    useEffect(() => {
        if (typeof window === 'undefined' || !basket?.basketId) return
        // Clear the cached session ID so a new one is generated for the new basket
        sessionStorage.removeItem('sig_session_id')
        const sessionId = basket?.c_sig_session_id
        setSignifydOrderSessionId(sessionId)
    }, [basket?.c_sig_session_id])

    const updateBasket = useShopperBasketsMutation('updateBasket')
    const updateCustomerForBasket = useShopperBasketsMutation('updateCustomerForBasket')

    useEffect(() => {
        // update the basket currency if it doesn't match the current locale currency
        if (basket?.currency && basket?.currency !== currency) {
            updateBasket.mutate({
                parameters: {basketId: basket.basketId},
                body: {currency}
            })
        }
    }, [basket?.currency])

    useEffect(() => {
        // update the basket customer email
        if (
            basket &&
            customer?.isRegistered &&
            customer?.email &&
            customer?.email !== basket?.customerInfo?.email
        ) {
            updateCustomerForBasket.mutate({
                parameters: {basketId: basket.basketId},
                body: {
                    email: customer.email
                }
            })
        }
    }, [customer?.isRegistered, customer?.email, basket?.customerInfo?.email])

    useEffect(() => {
        // Listen for online status changes.
        watchOnlineStatus((isOnline) => {
            setIsOnline(isOnline)
        })
    }, [])

    // Handle updating the shopper context
    useUpdateShopperContext()

    useGoogleTagManager(location.pathname)

    useContentClickTracking()
    usePromotionTracking()
    useLayoutShiftReporting()

    useEffect(() => {
        // Lets automatically close the mobile navigation when the
        // location path is changed.
        onClose()
    }, [location])

    const onLogoClick = () => {
        // Close the drawer.
        onClose()
    }

    const onCartClick = () => {
        const path = buildUrl('/cart')
        if (window?.dataLayer) {
            window.dataLayer.push({
                event: 'viewCartFromCartIcon',
                category: 'Cart',
                action: 'View Cart',
                label: 'Header Icon'
            })
        }
        // history.push(path)
        window.location.href = path

        // Close the drawer.
        onClose()
    }

    const onAccountClick = () => {
        if (customer?.isRegistered) {
            window.location.href = buildUrl('/account')
        } else {
            authModal.onOpen()
        }
    }

    const onWishlistClick = () => {
        // Link to wishlist page if registered; Header component will show auth modal for guest users
        const path = buildUrl('/account/wishlist')
        // history.push(path)
        window.location.href = path
    }

    const onStoreLocatorClick = () => {
        const path = buildUrl('/storelocator')
        // history.push(path)
        window.location.href = path
        // Close the drawer.
        onClose()
    }

    const trackPage = useCallback(() => {
        if (hasConsent && ACTIVE_DATA_ENABLED) {
            activeData.trackPage(site.id, locale.id, currency)
        }
    }, [hasConsent, activeData, site.id, locale.id, currency])

    useEffect(() => {
        const stickyElement = document.getElementById('sticky-header')
        const appMain = document.getElementById('app-main')?.parentElement
        if (!stickyElement || !appMain) return
        let originalOffset = stickyElement.offsetTop
        let isPinned = null
        let frame = null

        const applyStickyState = () => {
            frame = null
            if (window.scrollY > originalOffset + 15) {
                if (isPinned) return
                isPinned = true
                const headerHeight = stickyElement.offsetHeight
                stickyElement.style.position = 'fixed'
                stickyElement.style.top = '0'
                stickyElement.style.left = '0'
                stickyElement.style.width = '100%'
                stickyElement.style.zIndex = zIndex.header
                appMain.style.marginTop = headerHeight + 'px'
                setIsStickyActive(true)
            } else {
                originalOffset = stickyElement.offsetTop
                if (isPinned === false) return
                isPinned = false
                stickyElement.style.position = 'sticky'
                stickyElement.style.top = '0'
                stickyElement.style.width = ''
                appMain.style.marginTop = pxToRem(0)
                setIsStickyActive(false)
            }
        }

        const handleScroll = () => {
            if (frame !== null) return
            frame = window.requestAnimationFrame(applyStickyState)
        }

        window.addEventListener('scroll', handleScroll, {passive: true})
        applyStickyState()
        return () => {
            window.removeEventListener('scroll', handleScroll)
            if (frame !== null) window.cancelAnimationFrame(frame)
        }
    }, [promoBannerInHeaderIsActive, location])

    useEffect(() => {
        if (isServer || !ACTIVE_DATA_ENABLED) return
        const consent = hasAnalyticsConsent()
        setHasConsent(consent)
    }, [])

    useEffect(() => {
        if (isServer || !ACTIVE_DATA_ENABLED) return

        const unsubscribe = subscribeToConsentChanges((consent) => {
            setHasConsent(consent)
            if (!consent) {
                const dwanalytics = document.getElementById('dwanalytics')
                const dwac = document.getElementById('dwac')
                if (dwanalytics) dwanalytics.remove()
                if (dwac) dwac.remove()
                if (window.dw && window.dw.ac) {
                    window.dw.ac._analytics = null
                    window.dw.ac._events = []
                }
            }
        })

        return () => {
            if (unsubscribe) unsubscribe()
        }
    }, [])

    useEffect(() => {
        if (isServer || !ACTIVE_DATA_ENABLED || !hasConsent) return

        if (document.getElementById('dwanalytics') || document.getElementById('dwac')) {
            return
        }

        const dwanalytics = document.createElement('script')
        dwanalytics.type = 'text/javascript'
        dwanalytics.src = getAssetUrl('static/dwanalytics-22.2.js')
        dwanalytics.id = 'dwanalytics'
        dwanalytics.async = true
        dwanalytics.onload = () => {
            trackPage()
        }
        document.body.appendChild(dwanalytics)

        const dwac = document.createElement('script')
        dwac.src = getAssetUrl('static/dwac-21.7.js')
        dwac.type = 'text/javascript'
        dwac.id = 'dwac'
        dwac.async = true
        document.body.appendChild(dwac)
    }, [hasConsent, trackPage])

    useEffect(() => {
        if (hasConsent) {
            trackPage()
        }
    }, [location, hasConsent, trackPage])
    return (
        <Box className="sf-app" {...styles.container}>
            {gtmContainerID && (
                <noscript>
                    <iframe
                        title="Google Tag Manager"
                        src={`https://www.googletagmanager.com/ns.html?id=${gtmContainerID}${
                            gtmAuth && gtmPreview
                                ? `&gtm_auth=${gtmAuth}&gtm_preview=${gtmPreview}&gtm_cookies_win=x`
                                : ''
                        }`}
                        height="0"
                        width="0"
                        style={{display: 'none', visibility: 'hidden'}}
                    />
                </noscript>
            )}
            {isImpactTrackingEnabled && <ImpactTracker />}
            <BazaarvoiceTracker />
            <BootstrapProvider>
                <StorefrontPreviewProvider getToken={getTokenWhenReady}>
                    <Helmet>
                        {/* UserWay accessibility widget (ART) */}
                        {userWayAccountId && (
                            <script
                                src="https://cdn.userway.org/widget.js"
                                data-account={userWayAccountId}
                                data-position={userWayPosition}
                                data-color={userWayColor}
                            ></script>
                        )}
                        {ACTIVE_DATA_ENABLED && (
                            <script
                                src={getAssetUrl('static/head-active_data.js')}
                                id="headActiveData"
                                type="text/javascript"
                                defer
                            ></script>
                        )}
                        {/* Signifyd fraud detection script */}
                        {signifydOrderSessionId &&
                            isSignifydCartridgeEnabled &&
                            basket?.basketId && (
                                <script
                                    async
                                    type="text/javascript"
                                    id="sig-api"
                                    data-order-session-id={signifydOrderSessionId}
                                    src="https://cdn-scripts.signifyd.com/api/script-tag.js"
                                />
                            )}
                    </Helmet>
                    <IntlProvider
                        onError={(err) => {
                            if (!messages) {
                                return
                            }
                            if (err.code === 'MISSING_TRANSLATION') {
                                logger.warn('Missing translation', {
                                    namespace: 'App.IntlProvider',
                                    additionalProperties: {
                                        errorMessage: err.message
                                    }
                                })
                                return
                            }
                            throw err
                        }}
                        locale={targetLocale}
                        messages={messages}
                        // For react-intl, the _default locale_ refers to the locale that the inline `defaultMessage`s are written for.
                        // NOTE: if you update this value, please also update the following npm scripts in `template-retail-react-app/package.json`:
                        // - "extract-default-translations"
                        // - "compile-translations:pseudo"
                        defaultLocale={DEFAULT_LOCALE}
                    >
                        <CurrencyProvider currency={currency}>
                            <Seo>
                                <script
                                    src={appConfig.afterPay.url + '/square-marketplace.js'}
                                    async
                                ></script>
                                <meta name="theme-color" content={THEME_COLOR} />
                                <meta
                                    name="apple-mobile-web-app-title"
                                    content={DEFAULT_SITE_TITLE}
                                />
                                <link
                                    rel="apple-touch-icon"
                                    href={getAssetUrl('static/img/global/apple-touch-icon.png')}
                                />
                                <link rel="manifest" href={getAssetUrl('static/manifest.json')} />

                                {/* Urls for all localized versions of this page (including current page)
                            For more details on hrefLang, see https://developers.google.com/search/docs/advanced/crawling/localized-versions */}
                                {site.l10n?.supportedLocales.map((locale) => {
                                    const isStoreDetailsPage =
                                        location.pathname?.includes('/storedetails')
                                    const searchParams = isStoreDetailsPage ? location.search : ''
                                    return (
                                        <link
                                            rel="alternate"
                                            hrefLang={locale?.id?.toLowerCase()}
                                            href={`${PRODUCTION_URL}${buildUrl(
                                                location.pathname
                                            )}${searchParams}`}
                                            key={locale.id}
                                        />
                                    )
                                })}
                                {/* A general locale as fallback. For example: "en" if default locale is "en-GB" */}
                                {(() => {
                                    const isStoreDetailsPage =
                                        location.pathname?.includes('/storedetails')
                                    const searchParams = isStoreDetailsPage ? location.search : ''
                                    return (
                                        <link
                                            rel="alternate"
                                            hrefLang={site.l10n.defaultLocale.slice(0, 2)}
                                            href={`${PRODUCTION_URL}${buildUrl(
                                                location.pathname
                                            )}${searchParams}`}
                                        />
                                    )
                                })()}
                                {/* A wider fallback for user locales that the app does not support */}
                                {(() => {
                                    const isStoreDetailsPage =
                                        location.pathname?.includes('/storedetails')
                                    const searchParams = isStoreDetailsPage ? location.search : ''
                                    return (
                                        <link
                                            rel="alternate"
                                            hrefLang="x-default"
                                            href={`${PRODUCTION_URL}${buildUrl(
                                                location.pathname
                                            )}${searchParams}`}
                                        />
                                    )
                                })()}
                            </Seo>

                            <Fonts />

                            <ScrollToTop />
                            <Box id="app" display="flex" flexDirection="column" flex={1}>
                                <SkipNavLink zIndex="skipLink">Skip to Content</SkipNavLink>

                                <Box
                                    as="a"
                                    href={buildUrl('/content/digital-accessibility.html')}
                                    className="skip-link"
                                >
                                    Accessibility Statement
                                </Box>
                                {isCheckoutAndConfirmation ? (
                                    !location?.pathname?.includes('/paypal') && (
                                        <CheckoutHeader
                                            isCheckout={isCheckout}
                                            isPayPalPaymentPage={location?.pathname?.includes(
                                                '/paypal'
                                            )}
                                        />
                                    )
                                ) : (
                                    <Box {...styles.headerWrapper}>
                                        <PromoBanner
                                            slotIDs="header-promo-text"
                                            reservedHeight={HEADER_PROMO_BANNER_HEIGHT}
                                            setPromoBannerInHeaderIsActive={
                                                setPromoBannerInHeaderIsActive
                                            }
                                        />
                                        <Header
                                            onMenuClick={onOpen}
                                            onLogoClick={onLogoClick}
                                            onMyCartClick={onCartClick}
                                            onMyAccountClick={onAccountClick}
                                            onWishlistClick={onWishlistClick}
                                            onStoreLocatorClick={onStoreLocatorClick}
                                            menuToggle={menuToggle && isOpen}
                                            setMenuToggle={setMenuToggle}
                                        >
                                            <HideOnDesktop>
                                                <DrawerMenu
                                                    isOpen={isOpen}
                                                    onClose={onClose}
                                                    isStickyActive={isStickyActive}
                                                    promoBannerInHeaderIsActive={
                                                        promoBannerInHeaderIsActive
                                                    }
                                                    onLogoClick={onLogoClick}
                                                    root={
                                                        categories?.[CAT_MENU_DEFAULT_ROOT_CATEGORY]
                                                    }
                                                    itemsKey="categories"
                                                    itemsCountKey="onlineSubCategoriesCount"
                                                    itemComponent={ListMenuMainContent}
                                                    menuToggle={menuToggle}
                                                    setMenuToggle={setMenuToggle}
                                                />
                                            </HideOnDesktop>

                                            <HideOnMobileV2>
                                                <MegaMenu
                                                    root={
                                                        categories?.[CAT_MENU_DEFAULT_ROOT_CATEGORY]
                                                    }
                                                    itemsKey="categories"
                                                    itemsCountKey="onlineSubCategoriesCount"
                                                    contentComponent={MegaMenuContent}
                                                />
                                            </HideOnMobileV2>
                                        </Header>
                                    </Box>
                                )}
                                {!isOnline && <OfflineBanner />}
                                <AddToCartModalProvider>
                                    <SkipNavContent
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            flex: 1,
                                            outline: 0
                                        }}
                                    >
                                        <Box
                                            as="main"
                                            id="app-main"
                                            role="main"
                                            display="flex"
                                            flexDirection="column"
                                            flex="1"
                                            minHeight="100vh"
                                        >
                                            <OfflineBoundary isOnline={false}>
                                                <LoginProvider>{children}</LoginProvider>
                                            </OfflineBoundary>
                                        </Box>
                                    </SkipNavContent>

                                    {/* {!isCheckout ? <Footer /> : <CheckoutFooter />} */}
                                    {!location?.pathname?.includes('/paypal') && <Footer />}

                                    <AuthModal {...authModal} />
                                </AddToCartModalProvider>
                            </Box>
                        </CurrencyProvider>
                    </IntlProvider>
                    {/* Scripts loaded dynamically based on consent */}
                </StorefrontPreviewProvider>
            </BootstrapProvider>
        </Box>
    )
}

App.propTypes = {
    children: PropTypes.node
}

export default App
